export type IFBCallDirection =
  | "business_initiated"
  | "user_initiated"
  | "BUSINESS_INITIATED"
  | "USER_INITIATED";

export interface IFBCallSdpPayload {
  sdp_type?: "offer" | "answer";
  sdp?: string;
}

export interface IFBCallSession {
  sdp_type?: "offer" | "answer";
  sdp?: string;
  version?: number;
  sdp_renegotiation?: IFBCallSdpPayload | string;
  sdp_response?: IFBCallSdpPayload | string;
}

export interface IFBCallData {
  id: string;
  /** Consumer PSID (caller on inbound / callee context) */
  from?: string;
  /** Page ID */
  to?: string;
  event: "connect" | "terminate" | "call_status" | "media_update";
  timestamp: string | number;
  call_direction?: IFBCallDirection;
  direction?: IFBCallDirection;
  status?: "Failed" | "Completed" | string;
  call_status?: "ringing" | "accepted" | "rejected" | string;
  recipient_id?: string;
  start_time?: string | number;
  end_time?: string | number;
  duration?: number;
  session?: IFBCallSession;
  connection?: {
    webrtc?: {
      sdp?: string;
    };
  };
}

export interface iFBCallLog {
  id?: string;
  organizationId: string;
  connectionId: string;
  conversationId: string;
  contactId: string;
  ringAgents?: string[];
  callId: string;
  callStatus:
    | "OUTGOING_INIT"
    | "OUTGOING_INIT_SUCCESS"
    | "RINGING"
    | "INCOMING"
    | "IN_PROGRESS"
    | "REJECTED"
    | "COMPLETED";

  /** Consumer Page-Scoped ID */
  psid?: string;

  // Call Data
  callData?: IFBCallData;

  // MetaData
  startedAt: number;
  startedSource: "USER" | "CUSTOMER";
  startedBy: string; // userID or customerId
  /** Who accepted the call (picked up): agent USER vs end CUSTOMER */
  answeredSource?: "USER" | "CUSTOMER";
  answeredAt?: number;
  /** userId when agent answered, or contactId when customer answered (webhook may omit agent id) */
  answeredBy?: string;
  endedAt?: number;
  endedBy?: "USER" | "CUSTOMER";
  endedByUserId?: string;
  duration?: number;
  notes?: string;
  callRecording?: {
    callId: string;
    userId: string;
    startTime: number;
    endTime: number;
    durationMs: number;
    fileSize: number;
    mimeType: string;
    recordingUrl: string;
  };
}

/** Normalize Messenger/WA direction variants to uppercase enums for UI. */
export function normalizeFBCallDirection(
  call?: Pick<IFBCallData, "call_direction" | "direction"> | null,
): "BUSINESS_INITIATED" | "USER_INITIATED" | undefined {
  if (!call) return undefined;
  const raw = call.call_direction || call.direction;
  if (!raw) return undefined;
  const value = String(raw).toUpperCase();
  if (value === "BUSINESS_INITIATED") return "BUSINESS_INITIATED";
  if (value === "USER_INITIATED") return "USER_INITIATED";
  return undefined;
}

/** Extract SDP string from Messenger API session payloads (sdp_response or nested). */
export function extractMessengerSdpAnswer(
  session?: IFBCallSession | { sdp_response?: unknown; sdp?: string } | null,
): string | undefined {
  if (!session) return undefined;
  const response = session.sdp_response;
  if (typeof response === "string" && response.trim()) {
    return response;
  }
  if (
    response &&
    typeof response === "object" &&
    typeof (response as IFBCallSdpPayload).sdp === "string"
  ) {
    return (response as IFBCallSdpPayload).sdp;
  }
  if (typeof session.sdp === "string" && session.sdp.trim()) {
    return session.sdp;
  }
  return undefined;
}

/** User-facing status for lists/UI; differs from raw `callStatus` for COMPLETED calls. */
export function getFBCallLogDisplayStatus(log: iFBCallLog): string {
  if (log.callStatus === "COMPLETED") {
    const wasAnswered = log.answeredAt != null && log.answeredSource != null;
    if (wasAnswered) {
      return "Completed";
    }
    if (log.startedSource === "CUSTOMER") {
      return "Missed";
    }
    return "Not answered";
  }

  switch (log.callStatus) {
    case "OUTGOING_INIT":
    case "OUTGOING_INIT_SUCCESS":
      return "Outgoing";
    case "RINGING":
      return "Ringing";
    case "INCOMING":
      return "Incoming";
    case "IN_PROGRESS":
      return "In progress";
    case "REJECTED":
      return "Rejected";
    default:
      return log.callStatus;
  }
}
